import React, { useRef, useState } from "react";

import styled from "styled-components/macro";

import API from "Signups/API";
import { signupItemStateHooks } from "Signups/SignupView/SignupSheet/Screens/SignupItems/stateHooks";
import { useSignups } from "Signups/state";
import { useSignupListID } from "Signups/stateHooks";
import { AnimatePresence, motion } from "framer-motion";

import FnrButton3 from "UI/FnrButton3";
import { FnrPopoverWithScroll } from "UI/FnrPopover2";

import { isTouchDevice as touchDevice } from "Utilities/isTouchDevice";

import DonatedTooltipContent from "./DonatedTooltipContent";

export default function DonatedPill({
  status,
  amount,
  date,
  markedPaidName,
  additionalNameID,
  userName,
  payerName,
  payerUserID,
  signupItemID,
  style,
  isSuggestedDonation,
}) {
  const pillRef = useRef();
  const tooltipRef = useRef();
  const [state, dispatch] = useSignups();
  const { signupListID } = useSignupListID();
  const isTouchDevice = touchDevice();
  const currentUserCanViewGiving = state?.user?.canViewGiving;

  const [hovering, setHovering] = useState(false);
  const [hoveringTooltip, setHoveringTooltip] = useState(false);
  const [showMarkUnpaidUI, setShowMarkUnpaidUI] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const { getSignupItem } = signupItemStateHooks.useGetSignupItem();

  const showPopover = hovering || showMarkUnpaidUI || hoveringTooltip;
  const showTooltip = status === "MarkedAsPaid" && isTouchDevice;

  const otherPersonPaid = payerName !== userName;

  const handleClick = () => {
    if (status !== "MarkedAsPaid") {
      setHovering(true);
      return;
    }

    if (!showMarkUnpaidUI) {
      setShowMarkUnpaidUI(true);
      return;
    }

    setShowMarkUnpaidUI(false);
    setHovering(false);
  };

  const markAsUnpaid = () => {
    setLoading(true);
    setError(undefined);

    let payload = {
      userSignupItemsPaymentStatus: "Unpaid",
      additionalNameID,
    };

    API.markAsPaid(signupListID, payload)
      .post()
      .then((resp) => {
        getSignupItem(signupItemID);
        dispatch({
          type: "Other User Signup Updated",
          signupList: resp.signupList,
          allSignupLists: resp.allSignupLists,
        });
        setShowMarkUnpaidUI(false);
      })
      .catch((err) => {
        setError(
          "Something went wrong. Please try again. Contact support@flocknote.com if the problem continues."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  function handlePillMouseLeave() {
    setTimeout(() => {
      setHovering(false);
    }, 200);
  }

  function handleTooltipMouseLeave() {
    setTimeout(() => {
      setHoveringTooltip(false);
    }, 200);
  }

  const goToProfileGivingTab = () =>
    window.fnReactBus.goToProfileGivingTab(payerUserID);

  const showProfileLinkIcon =
    currentUserCanViewGiving && payerUserID && (amount || isSuggestedDonation);

  return (
    <TooltipWrapper>
      <Pill
        ref={pillRef}
        onMouseEnter={() => setHovering(true)}
        onMouseOut={handlePillMouseLeave}
        onClick={handleClick}
        markedAsPaid={status === "MarkedAsPaid"}
        style={style}
      >
        Donated
      </Pill>
      <FnrPopoverWithScroll
        buttonRef={pillRef}
        show={showPopover}
        location="top"
        scrollingElClassname={".SignupSheet"}
        onShouldClose={() => {
          setHovering(false);
          setHoveringTooltip(false);
          setShowMarkUnpaidUI(false);
          setError(undefined);
        }}
        style={{ width: "250px" }}
        bgTransparency={1}
      >
        <AnimatePresence>
          {showMarkUnpaidUI ? (
            <MotionDiv
              key="unpaidui"
              initial={{
                opacity: isTouchDevice ? 1 : 0,
                height: isTouchDevice ? "auto" : 0,
                scaleY: isTouchDevice ? 1 : 0.5,
              }}
              animate={{ opacity: 1, height: "auto", scaleY: 1 }}
            >
              {showTooltip ? (
                <div style={{ marginBottom: "10px" }}>
                  <DonatedTooltipContent
                    goToProfileGivingTab={goToProfileGivingTab}
                    showProfileLinkIcon={showProfileLinkIcon}
                    status={status}
                    markedPaidName={markedPaidName}
                    otherPersonPaid={otherPersonPaid}
                    payerName={payerName}
                  />
                </div>
              ) : null}

              <div>Do you want to mark this person as having not donated?</div>
              <div style={{ marginTop: "10px" }} />

              <AnimatePresence>
                {error ? (
                  <Error
                    key="error"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                  >
                    <i
                      className="far fa-exclamation-circle"
                      style={{ marginRight: "5px" }}
                    ></i>
                    {error}
                  </Error>
                ) : null}
              </AnimatePresence>

              <ButtonWrapper>
                <FnrButton3
                  size="small"
                  onClick={() => {
                    setShowMarkUnpaidUI(false);
                    setError(undefined);
                  }}
                >
                  No
                </FnrButton3>
                <FnrButton3
                  size="small"
                  color="green"
                  onClick={markAsUnpaid}
                  loading={loading}
                >
                  Yes
                </FnrButton3>
              </ButtonWrapper>
            </MotionDiv>
          ) : (
            <MotionDiv
              key="tooltip"
              ref={tooltipRef}
              exit={{ opacity: 0, height: 0 }}
              onMouseEnter={() => setHoveringTooltip(true)}
              onMouseLeave={handleTooltipMouseLeave}
            >
              <DonatedTooltipContent
                showProfileLinkIcon={showProfileLinkIcon}
                goToProfileGivingTab={goToProfileGivingTab}
                status={status}
                markedPaidName={markedPaidName}
                otherPersonPaid={otherPersonPaid}
                payerName={payerName}
              />
            </MotionDiv>
          )}
        </AnimatePresence>
      </FnrPopoverWithScroll>
    </TooltipWrapper>
  );
}

const TooltipWrapper = styled.div`
  position: relative;
`;

const Pill = styled.div`
  background: #209e20;
  padding-inline: 6px;
  padding-block: 1px;
  font-size: 9px;
  font-weight: 600;
  font-style: normal;
  color: rgb(255 255 255 / 90%);
  border-radius: 16px;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  &:hover {
    cursor: ${(props) => (props.markedAsPaid ? "pointer" : "default")};
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const Error = styled(motion.div)`
  border: 1px solid white;
  padding: 0.25rem;
  border-radius: 8px;
  margin-bottom: 10px;
  font-size: 14px;
`;

const MotionDiv = styled(motion.div)`
  /* --donationPillPopoverText: #073a5f; */
  --donationPillPopoverText: rgba(255, 255, 255, 0.9);
  color: var(--donationPillPopoverText);
  padding: 6px;
`;